import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { initializeApp, getApps } from 'firebase/app';
import { getAuth, createUserWithEmailAndPassword, updateProfile } from 'firebase/auth';
import { useTenant } from '../contexts/TenantContext';

export default function SignupPage() {
    const { tenant, firebaseConfig } = useTenant();
    const navigate = useNavigate();
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    async function handleSubmit(e: React.FormEvent) {
        e.preventDefault();
        setError('');
        setSuccess('');

        if (password !== confirmPassword) {
            setError('Passwords do not match');
            return;
        }

        if (password.length < 6) {
            setError('Password must be at least 6 characters');
            return;
        }

        if (!firebaseConfig) {
            setError('Firebase not configured');
            return;
        }

        setLoading(true);

        try {
            // Initialize Firebase if not already done
            const app = getApps().length === 0 ? initializeApp(firebaseConfig) : getApps()[0];
            const auth = getAuth(app);

            // Create user in Firebase
            const userCredential = await createUserWithEmailAndPassword(auth, email, password);

            // Update display name
            await updateProfile(userCredential.user, { displayName: name });

            // Get ID token and verify with backend
            const idToken = await userCredential.user.getIdToken();
            const response = await fetch(`${import.meta.env.VITE_API_URL}/auth/verify`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ idToken, tenantId: tenant?.id, name }),
            });

            const data = await response.json();

            console.log('Signup verify response:', {
                status: response.status,
                data,
                isPending: response.status === 202 || data.status === 'pending'
            });

            if (response.status === 202 || data.status === 'pending') {
                // User needs approval
                setSuccess('Account created! Your account is pending admin approval. You will receive an email once approved.');
            } else if (response.ok) {
                // User approved immediately (invited or no approval required)
                setSuccess('Account created successfully! Redirecting...');
                setTimeout(() => navigate('/'), 2000);
            } else if (response.status === 403 && data.error === 'Domain Not Allowed') {
                // Domain not in whitelist
                setError(data.message || 'Your email domain is not allowed to register.');
                // Sign out the Firebase user since they can't use the app
                await auth.signOut();
            } else {
                setError(data.message || 'Failed to create account');
            }
        } catch (err: any) {
            console.error('Signup error:', err);
            if (err.code === 'auth/email-already-in-use') {
                setError('An account with this email already exists. Please sign in instead.');
            } else if (err.code === 'auth/invalid-email') {
                setError('Invalid email address');
            } else if (err.code === 'auth/weak-password') {
                setError('Password is too weak');
            } else {
                setError(err.message || 'Failed to create account');
            }
        } finally {
            setLoading(false);
        }
    }

    return (
        <div className="min-h-screen flex items-center justify-center">
            <div className="max-w-md w-full space-y-8 p-8 bg-surface rounded-lg shadow-md border border-border">
                {tenant?.logoUrl && (
                    <div className="flex justify-center">
                        <img src={tenant.logoUrl} alt={tenant.name} className="h-12" />
                    </div>
                )}

                <div>
                    <h2 className="mt-6 text-center text-3xl font-extrabold text-text-primary">
                        Create your account
                    </h2>
                    <p className="mt-2 text-center text-sm text-text-secondary">
                        {tenant?.name || 'Stellar Bingo'}
                    </p>
                </div>

                <form onSubmit={handleSubmit} className="mt-8 space-y-6">
                    {error && (
                        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
                            {error}
                        </div>
                    )}

                    {success && (
                        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded">
                            {success}
                        </div>
                    )}

                    <div className="space-y-4">
                        <div>
                            <label htmlFor="name" className="block text-sm font-medium text-text-secondary mb-1">
                                Full Name
                            </label>
                            <input
                                id="name"
                                type="text"
                                required
                                className="appearance-none relative block w-full px-3 py-2 border border-border placeholder-gray-500 text-text-primary rounded-md focus:outline-none focus:ring-primary focus:border-primary sm:text-sm bg-surface"
                                placeholder="John Doe"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                            />
                        </div>
                        <div>
                            <label htmlFor="email" className="block text-sm font-medium text-text-secondary mb-1">
                                Email Address
                            </label>
                            <input
                                id="email"
                                type="email"
                                required
                                className="appearance-none relative block w-full px-3 py-2 border border-border placeholder-gray-500 text-text-primary rounded-md focus:outline-none focus:ring-primary focus:border-primary sm:text-sm bg-surface"
                                placeholder="john@company.com"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                            />
                        </div>
                        <div>
                            <label htmlFor="password" className="block text-sm font-medium text-text-secondary mb-1">
                                Password
                            </label>
                            <input
                                id="password"
                                type="password"
                                required
                                className="appearance-none relative block w-full px-3 py-2 border border-border placeholder-gray-500 text-text-primary rounded-md focus:outline-none focus:ring-primary focus:border-primary sm:text-sm bg-surface"
                                placeholder="••••••••"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                            />
                        </div>
                        <div>
                            <label htmlFor="confirmPassword" className="block text-sm font-medium text-text-secondary mb-1">
                                Confirm Password
                            </label>
                            <input
                                id="confirmPassword"
                                type="password"
                                required
                                className="appearance-none relative block w-full px-3 py-2 border border-border placeholder-gray-500 text-text-primary rounded-md focus:outline-none focus:ring-primary focus:border-primary sm:text-sm bg-surface"
                                placeholder="••••••••"
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                            />
                        </div>
                    </div>

                    <button
                        type="submit"
                        disabled={loading || !!success}
                        className="btn-primary w-full"
                    >
                        {loading ? 'Creating account...' : 'Create Account'}
                    </button>

                    <p className="text-center text-sm text-text-secondary">
                        Already have an account?{' '}
                        <Link to="/login" className="text-primary hover:underline font-medium">
                            Sign in
                        </Link>
                    </p>
                </form>
            </div>
        </div>
    );
}
